import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnalyticsIcon } from '../components/icons/AnalyticsIcon'
import { EyeIcon } from '../components/icons/EyeIcon'
import { McpNameWithBadge } from '../components/mcp/mcpShared'
import { McpConnectionsPage } from '../components/mcp/McpConnectionsPage'
import type { MambuConnectionCatalogItem } from '../lib/types'
import { MCP_ANALYTICS_DEFAULT_TAB_PATH } from './McpAnalyticsDetail'

/* ─── MCP Analytics — connections overview (MS-700 AC-2/3/4/6) ───
   Prototype: agent-studio.html:5746-5860 (#mcpobs-overview).
   Thin wrapper around McpConnectionsPage. Only the live-item action (open analytics
   view) and the AnalyticsIcon avatar are specific to this surface.

   Reads the SAME catalog endpoint as MCP Config (/api/v1/mambu-connections):
   the three MCP products and their live/preview status are one list, gated by
   the same Admin check, so there is no second endpoint and — because the query
   key is shared — no refetch when moving between the two surfaces.

   Renders NO event data. That is deliberate: it keeps this page independent of
   the still-undecided storage/serving architecture for the MCP event stream
   (MS-698), which is why it can ship first.

   READ-ONLY SURFACE (MS-700 AC-15, spec §4.1-3c): this page must never call
   useDirty(), never call usePageNavigationGuard(), and never raise a navigation
   confirm dialog. Leaving it must never prompt. Note the prototype disagrees —
   agent-studio.html:8601 includes #section-mcp-analytics in its needsGuard set —
   and the spec and the AC win over the prototype on this point. */

/* AC-4 — icon consistency. EVERY item, subscribed or Preview, plus the page
   header when it lands (MS-701), uses the MCP Analytics NAV icon. The MCP
   *product* icon (HeartbeatIcon) belongs to MCP Config and must not appear
   here: nav, overview and page have to read as one surface.
   Prototype: agent-studio.html:5763.
   Sizes: grid 22 vs list 18 — agent-studio.html:5765 vs :5814. */
function AnalyticsAvatar({ size }: { readonly size: 22 | 18 }) {
  return (
    <div className="agent-grid-icon" aria-hidden="true" style={{ color: 'var(--accent-text)' }}>
      <AnalyticsIcon size={size} strokeWidth={2} />
    </div>
  )
}

export function McpAnalytics() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  /* AC-6: opening a connection produces a URL that NAMES THE TAB. Never navigate
     to a bare /mcp-analytics/core — the address bar must describe the view, and a
     reload of it must land on the same tab rather than the overview. */
  function openCoreAnalytics() {
    navigate(MCP_ANALYTICS_DEFAULT_TAB_PATH)
  }

  function renderLiveCard(item: MambuConnectionCatalogItem) {
    return (
      <div
        key={item.key}
        role="button"
        tabIndex={0}
        aria-label={t('mcpAnalytics.core.openAria')}
        onClick={openCoreAnalytics}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); openCoreAnalytics() } }}
        className="agent-grid-card mcpobs-clickable"
      >
        <div className="agent-grid-card-header">
          <AnalyticsAvatar size={22} />
        </div>
        <McpNameWithBadge item={item} />
        {/* Surface copy shared with MCP Config — "Live access to core banking data
            and operations" (agent-studio.html:5766) — not the connection's stored
            description, which the prototype renders on the Agent → Connections tab
            instead (:4047). */}
        <div className="agent-grid-desc">{t('mambuMcp.core.description')}</div>
        <div className="agent-grid-footer">
          <div />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="skill-edit-btn"
              title={t('mcpAnalytics.core.openTitle')}
              aria-label={t('mcpAnalytics.core.openAria')}
              onClick={openCoreAnalytics}
              style={{ color: 'var(--accent)' }}
            >
              <EyeIcon />
            </button>
          </div>
        </div>
      </div>
    )
  }

  function renderLiveRow(item: MambuConnectionCatalogItem) {
    return (
      <div
        key={item.key}
        role="button"
        tabIndex={0}
        aria-label={t('mcpAnalytics.core.openAria')}
        onClick={openCoreAnalytics}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); openCoreAnalytics() } }}
        className="agent-list-row mcpobs-clickable"
      >
        <AnalyticsAvatar size={18} />
        <div className="agent-list-info">
          <McpNameWithBadge item={item} />
          {/* Surface copy, not item.description — see the grid card above. */}
          <div className="agent-grid-desc">{t('mambuMcp.core.description')}</div>
        </div>
        <div className="agent-list-actions" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="skill-edit-btn"
            title={t('mcpAnalytics.core.openTitle')}
            aria-label={t('mcpAnalytics.core.openAria')}
            onClick={openCoreAnalytics}
            style={{ color: 'var(--accent)' }}
          >
            <EyeIcon />
          </button>
        </div>
      </div>
    )
  }

  return (
    <McpConnectionsPage
      heading={t('mcpAnalytics.heading')}
      subtitle={t('mcpAnalytics.subtitle')}
      failedToLoad={t('mcpAnalytics.failedToLoad')}
      gridViewLabel={t('mcpAnalytics.gridView')}
      listViewLabel={t('mcpAnalytics.listView')}
      cssPrefix="mcpobs"
      voteSurface="analytics"
      disabledActionIcon={<EyeIcon />}
      renderAvatar={(size) => <AnalyticsAvatar size={size} />}
      renderLiveCard={renderLiveCard}
      renderLiveRow={renderLiveRow}
    />
  )
}
